import { calculateCardCompletion } from "@shared/field-definitions";
import type { WizardSlice } from "./types";
import { groupTransitionMeasuresByTopic } from "./transitionMeasures.utils";
import type {
  FuelEntry,
  ElectricityEntry,
  RefrigerantEntry,
  DocumentMetadata,
  PollutionData,
  BiodiversityData,
  WaterData,
  WasteData,
  Scope3Data,
  RiskManagementData,
  TransitionMeasure,
} from "@/contexts/wizard/types";

/**
 * Environment slice state (all data from environment tab + carbon card)
 */
export interface EnvironmentSliceState {
  fuelEntries: FuelEntry[];
  electricityEntries: ElectricityEntry[];
  refrigerantEntries: RefrigerantEntry[];
  carbonSupportingDocuments: DocumentMetadata[];
  pollutionData: PollutionData;
  biodiversityData: BiodiversityData;
  biodiversitySupportingDocuments: DocumentMetadata[];
  waterData: WaterData;
  waterSupportingDocuments: DocumentMetadata[];
  wasteData: WasteData;
  wasteSupportingDocuments: DocumentMetadata[];
  // For completion calculation
  totalEmployees: string;
  homeworkingPercent: string;
  // Scope 3 data (optional - passed separately if available)
  scope3Data?: Scope3Data;
  // Risk Management (C4) - climate-related risk data
  riskManagementData: RiskManagementData;
  // Transition measures, keyed by topicId (carbon-emissions, pollution, biodiversity, water, waste, risk-management)
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
}

/**
 * API response structure for environment data
 */
export interface EnvironmentApiData {
  carbonEmissionsData?: {
    fuelEntries?: FuelEntry[];
    electricityEntries?: ElectricityEntry[];
    refrigerantEntries?: RefrigerantEntry[];
    supportingDocuments?: DocumentMetadata[];
    scope1Total?: number;
    scope2Total?: number;
    scope2KwhAnnual?: number;
    scope3Homeworking?: number;
    homeworkingPercent?: number;
    fte?: number;
    calculatedAt?: string;
  };
  pollutionData?: PollutionData;
  biodiversityData?: BiodiversityData & { supportingDocuments?: DocumentMetadata[] };
  waterData?: WaterData & { supportingDocuments?: DocumentMetadata[] };
  wasteData?: WasteData & { supportingDocuments?: DocumentMetadata[] };
  scope3Data?: Scope3Data;
  riskManagementData?: RiskManagementData;
  transitionMeasures?: TransitionMeasure[];
}

/**
 * State setters for environment slice
 */
export interface EnvironmentStateSetter {
  setFuelEntries: (entries: FuelEntry[]) => void;
  setElectricityEntries: (entries: ElectricityEntry[]) => void;
  setRefrigerantEntries: (entries: RefrigerantEntry[]) => void;
  setCarbonSupportingDocuments: (docs: DocumentMetadata[]) => void;
  setPollutionData: (data: PollutionData) => void;
  setBiodiversityData: (data: BiodiversityData) => void;
  setBiodiversitySupportingDocuments: (docs: DocumentMetadata[]) => void;
  setWaterData: (data: WaterData) => void;
  setWaterSupportingDocuments: (docs: DocumentMetadata[]) => void;
  setWasteData: (data: WasteData) => void;
  setWasteSupportingDocuments: (docs: DocumentMetadata[]) => void;
  setScope3DataFromApi?: (data: Scope3Data) => void;
  setRiskManagementData: (data: RiskManagementData) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
}

// ==================== API FUNCTIONS ====================

async function fetchEnvironment(assessmentId: string): Promise<EnvironmentApiData> {
  const response = await fetch(`/api/assessments/${assessmentId}/environment`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch environment data");
  return response.json();
}

async function fetchEmployeeData(assessmentId: string) {
  const response = await fetch(`/api/assessments/${assessmentId}/employee-data`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch employee data");
  return response.json();
}

async function saveEnvironment(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}/environment`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

// ==================== HYDRATION MAPPER ====================

const defaultPollutionData: PollutionData = {
  reportingType: null,
};

const defaultBiodiversityData: BiodiversityData = {
  sitesInSensitiveAreas: null,
  areaOfSensitiveSites: null,
  totalLandUse: null,
  sealedArea: null,
  natureOrientedOnSite: null,
  natureOrientedOffSite: null,
  notes: "",
  evidenceUrl: "",
  supportingDocuments: [],
};

const defaultWaterData: WaterData = {
  totalWithdrawal: null,
  withdrawalInStressAreas: null,
  hasConsumptionProcess: null,
  annualConsumption: null,
  waterSource: "",
  notes: "",
  evidenceUrl: "",
  supportingDocuments: [],
};

const defaultWasteData: WasteData = {
  appliesCircularEconomy: false,
  circularEconomyPrinciples: [],
  circularEconomyDescription: "",
  nonHazardousWaste: null,
  nonHazardousWasteUnit: "kg",
  hazardousWaste: null,
  hazardousWasteUnit: "kg",
  wasteDiverted: null,
  wasteDivertedUnit: "kg",
  tracksMaterials: null,
  materialsDescription: "",
  notes: "",
  evidenceUrl: "",
  supportingDocuments: [],
};

const defaultRiskManagementData: RiskManagementData = {
  climateRisksIdentified: null,
  mainHazards: "",
  riskAssessmentProcess: "",
  timeHorizon: [],
  adaptationActions: "",
  riskLevel: null,
  potentialEffects: "",
};

function hydrateEnvironmentState(
  apiData: EnvironmentApiData,
  setter: EnvironmentStateSetter
): void {
  const carbon = apiData.carbonEmissionsData || {};

  setter.setFuelEntries(carbon.fuelEntries || []);
  setter.setElectricityEntries(carbon.electricityEntries || []);
  setter.setRefrigerantEntries(carbon.refrigerantEntries || []);
  setter.setCarbonSupportingDocuments(carbon.supportingDocuments || []);

  setter.setPollutionData(apiData.pollutionData || defaultPollutionData);

  const bio = apiData.biodiversityData || defaultBiodiversityData;
  const bioSupporting = bio.supportingDocuments || [];
  setter.setBiodiversityData(bio);
  setter.setBiodiversitySupportingDocuments(bioSupporting);

  const water = apiData.waterData || defaultWaterData;
  const waterSupporting = water.supportingDocuments || [];
  setter.setWaterData(water);
  setter.setWaterSupportingDocuments(waterSupporting);

  const waste = apiData.wasteData || defaultWasteData;
  const wasteSupporting = waste.supportingDocuments || [];
  setter.setWasteData(waste);

  // Hydrate Scope 3 data if available
  if (apiData.scope3Data && setter.setScope3DataFromApi) {
    setter.setScope3DataFromApi(apiData.scope3Data);
  }
  setter.setWasteSupportingDocuments(wasteSupporting);

  // Risk Management data
  setter.setRiskManagementData(apiData.riskManagementData || defaultRiskManagementData);

  // Transition measures: flat array from the API, grouped by topicId for reducer state
  groupTransitionMeasuresByTopic(apiData.transitionMeasures || [], setter.setTransitionMeasuresForTopic);
}

// ==================== SIGNATURE & DIRTY TRACKING ====================

/**
 * Compute signature for environment state.
 * Now excludes calculated totals since they're computed on backend.
 * Only tracks user-entered data to detect real changes.
 */
function computeEnvironmentSignature(state: EnvironmentSliceState): string {
  const fte = parseFloat(state.totalEmployees) || 0;
  const homeworkingPct = parseFloat(state.homeworkingPercent) || 0;

  const carbonEmissionsData = {
    fuelEntries: state.fuelEntries.filter((e) => !e.calculating),
    electricityEntries: state.electricityEntries.filter((e) => !e.calculating),
    refrigerantEntries: state.refrigerantEntries.filter((e) => !e.calculating),
    fte: fte,
    homeworkingPercent: homeworkingPct,
    supportingDocuments: state.carbonSupportingDocuments,
    // Backend-calculated fields excluded: scope1Total, scope2Total, scope2KwhAnnual, scope3Homeworking, calculatedAt
  };

  const payload = {
    carbonEmissionsData,
    pollutionData: state.pollutionData,
    biodiversityData: {
      ...state.biodiversityData,
      supportingDocuments: state.biodiversitySupportingDocuments,
    },
    waterData: { ...state.waterData, supportingDocuments: state.waterSupportingDocuments },
    wasteData: { ...state.wasteData, supportingDocuments: state.wasteSupportingDocuments },
    scope3Data: state.scope3Data,
    riskManagementData: state.riskManagementData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
  };

  return JSON.stringify(payload);
}

function isEnvironmentDirty(state: EnvironmentSliceState, baselineSignature: string): boolean {
  const currentSignature = computeEnvironmentSignature(state);
  return currentSignature !== baselineSignature;
}

// ==================== SAVE ====================

async function saveEnvironmentSlice(assessmentId: string, state: EnvironmentSliceState): Promise<void> {
  // Note: Carbon totals (scope1Total, scope2Total, scope2KwhAnnual, scope3Homeworking)
  // are now calculated on the BACKEND for consistency and data integrity.
  // We just pass the raw data and let the backend handle calculations.

  const fte = parseFloat(state.totalEmployees) || 0;
  const homeworkingPct = parseFloat(state.homeworkingPercent) || 0;

  const carbonEmissionsData = {
    fuelEntries: state.fuelEntries.filter((e) => !e.calculating),
    electricityEntries: state.electricityEntries.filter((e) => !e.calculating),
    refrigerantEntries: state.refrigerantEntries.filter((e) => !e.calculating),
    // Send employee data for Scope 3 calculation
    fte: fte,
    homeworkingPercent: homeworkingPct,
    supportingDocuments: state.carbonSupportingDocuments,
    // Backend will calculate and add: scope1Total, scope2Total, scope2KwhAnnual, scope3Homeworking, calculatedAt
  };

  const payload = {
    carbonEmissionsData,
    pollutionData: state.pollutionData,
    biodiversityData: {
      ...state.biodiversityData,
      supportingDocuments: state.biodiversitySupportingDocuments,
    },
    waterData: { ...state.waterData, supportingDocuments: state.waterSupportingDocuments },
    wasteData: { ...state.wasteData, supportingDocuments: state.wasteSupportingDocuments },
    scope3Data: state.scope3Data,
    riskManagementData: state.riskManagementData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
    riskManagementCompletenessPercent: calculateCardCompletion('riskManagement', state.riskManagementData),
    businessTravelCompletenessPercent: calculateCardCompletion('businessTravel', state.scope3Data),
    accommodationCompletenessPercent: calculateCardCompletion('accommodation', state.scope3Data),
    employeeCommutingCompletenessPercent: calculateCardCompletion('employeeCommuting', state.scope3Data),
    freightCompletenessPercent: calculateCardCompletion('freight', state.scope3Data),
  };

  const response = await saveEnvironment(assessmentId, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to save environment" }));
    throw new Error(error.error || "Failed to save environment");
  }
}

// ==================== COMPLETION DATA ====================

function getEnvironmentCompletionData(state: EnvironmentSliceState) {
  // Return data used by backend to calculate completion
  return {
    carbonEmissionsData: {
      fuelEntries: state.fuelEntries,
      electricityEntries: state.electricityEntries,
      refrigerantEntries: state.refrigerantEntries,
    },
    pollutionData: state.pollutionData,
    biodiversityData: state.biodiversityData,
    waterData: state.waterData,
    wasteData: state.wasteData,
    riskManagementData: state.riskManagementData,
  };
}

// ==================== SLICE EXPORT ====================

export const environmentSlice: WizardSlice<EnvironmentSliceState, EnvironmentApiData, EnvironmentStateSetter> = {
  queryKey: (assessmentId: string) => ["environment", assessmentId],
  fetch: fetchEnvironment,
  hydrate: hydrateEnvironmentState,
  isDirty: isEnvironmentDirty,
  computeSignature: computeEnvironmentSignature,
  save: saveEnvironmentSlice,
  getCompletionData: getEnvironmentCompletionData,
  impactsCompletion: true,
};

// Export employee data fetcher for general slice coordination
export { fetchEmployeeData };
